import React, { useState } from 'react';
import { Calendar, DollarSign, User, Package, FileText, Download, Printer, ArrowRight, Info, ExternalLink, CheckCircle, Clock } from 'lucide-react';
import { formatCurrency, formatPercentage, formatDateTime } from '../../utils/formatters';
import { supabase } from '../../lib/supabase';
import { 
  generateQuotationPDF, 
  generateQuotationExcel, 
  generateQuotationWord, 
  downloadFile, 
  printQuotation 
} from '../../utils/documentGenerator';
import { 
  convertQuotationToSalesOrder, 
  validateQuotationForConversion, 
  updateQuotationStatus 
} from '../../utils/quotationConverter';
import { trackQuotationToSalesOrder } from '../../utils/processFlowTracker';

const QuotationDetail = ({ quotation, getStatusDetails, getCustomerDetails, setActiveView, onQuotationUpdate }) => {
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionMessage, setConversionMessage] = useState('');
  const [convertedSalesOrder, setConvertedSalesOrder] = useState(null);
  const [showTraceInfo, setShowTraceInfo] = useState(false);
  
  if (!quotation) return null;
  
  const status = getStatusDetails(quotation.status_id);
  const customer = getCustomerDetails ? getCustomerDetails(quotation.customer_id) : null;

  // Print functions
  const handlePrint = () => {
    printQuotation(quotation);
  };

  const handleDownloadPDF = () => {
    const content = generateQuotationPDF(quotation);
    downloadFile(content, `報價單_${quotation.quotation_id}.html`, 'text/html');
  };

  const handleDownloadExcel = () => {
    const content = generateQuotationExcel(quotation);
    // For Excel, we'll create a CSV format that can be opened in Excel
    const csvContent = content.sheets[0].data.map(row => 
      row.map(cell => `"${cell || ''}"`).join(',')
    ).join('\n');
    downloadFile(csvContent, `報價單_${quotation.quotation_id}.csv`, 'text/csv');
  };

  const handleDownloadWord = () => {
    const content = generateQuotationWord(quotation);
    downloadFile(content, `報價單_${quotation.quotation_id}.html`, 'text/html');
  };

  // Convert to purchase order
  const handleConvertToOrder = async () => {
    setIsConverting(true);
    setConversionMessage('');

    try {
      // Validate quotation
      const validationErrors = validateQuotationForConversion(quotation);
      if (validationErrors.length > 0) {
        setConversionMessage(`轉換失敗: ${validationErrors.join(', ')}`);
        return;
      }

      // Convert quotation to sales order
      const salesOrder = convertQuotationToSalesOrder(quotation);
      
      // Update quotation status
      const updatedQuotation = updateQuotationStatus(quotation);
      
      // Update quotation status in Supabase database
      try {
        const { error: updateError } = await supabase
          .from('quotations')
          .update({ status_id: 'SO' })
          .eq('id', quotation.id);

        if (updateError) {
          console.error('Error updating quotation status in database:', updateError);
        }
      } catch (error) {
        console.error('Error updating quotation status:', error);
      }
      
      // Call parent function to update quotation
      if (onQuotationUpdate) {
        onQuotationUpdate(updatedQuotation);
      }

      // Track the process flow
      try {
        await trackQuotationToSalesOrder(quotation, salesOrder, 'system');
      } catch (error) {
        console.error('Error tracking process flow:', error);
        // Don't fail the conversion if tracking fails
      }

      // Store sales order in localStorage for now (in real app, this would go to database)
      const existingSOs = JSON.parse(localStorage.getItem('salesOrders') || '[]');
      existingSOs.push(salesOrder);
      localStorage.setItem('salesOrders', JSON.stringify(existingSOs));

      // Store the converted sales order for tracing
      setConvertedSalesOrder(salesOrder);
      setConversionMessage(`成功轉換為銷售訂單: ${salesOrder.sales_order_id} (狀態已更新為SO)`);
      
      // Show success message for 5 seconds, then show trace info
      setTimeout(() => {
        setConversionMessage('');
        setShowTraceInfo(true);
      }, 5000);

    } catch (error) {
      setConversionMessage(`轉換失敗: ${error.message}`);
    } finally {
      setIsConverting(false);
    }
  };

  // Handle tracing actions
  const handleViewSalesOrder = () => {
    // Navigate to sales order view
    if (convertedSalesOrder) {
      // Store the sales order in localStorage for the sales order view to access
      localStorage.setItem('currentSalesOrder', JSON.stringify(convertedSalesOrder));
      
      // Navigate to sales order view
      setActiveView('sales-order');
    }
  };

  const handleCopySONumber = () => {
    navigator.clipboard.writeText(convertedSalesOrder.sales_order_id);
    alert('銷售訂單編號已複製到剪貼簿');
  };

  const handleHideTraceInfo = () => {
    setShowTraceInfo(false);
    setConvertedSalesOrder(null);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">報價單詳情</h2>
        <button 
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          onClick={() => setActiveView('list')}
        >
          返回列表
        </button>
      </div>
      
      {/* Quotation Header */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{quotation.quotation_number}</h3>
              <span 
                className="mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full" 
                style={{ backgroundColor: `${status.color_code}20`, color: status.color_code }}
              >
                {status.status_name}
              </span>
            </div>
            
            <div className="flex flex-col items-end">
              <p className="text-sm text-gray-500">創建日期: {quotation.issue_date}</p>
              <p className="text-sm text-gray-500">有效期至: {quotation.expiry_date}</p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Customer Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-full mr-3">
                  <User className="h-5 w-5 text-blue-600" />
                </div>
                <h4 className="font-medium text-gray-900">客戶資料</h4>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-900">{quotation.customer_name || '未知客戶'}</p>
                <p className="text-sm text-gray-600">聯絡人: {quotation.contact_person || '未知'}</p>
                <p className="text-sm text-gray-600">電郵: {quotation.contact_email || '未知'}</p>
                <p className="text-sm text-gray-600">電話: {quotation.contact_phone || '未知'}</p>
              </div>
            </div>
            
            {/* Quotation Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="bg-green-100 p-2 rounded-full mr-3">
                  <FileText className="h-5 w-5 text-green-600" />
                </div>
                <h4 className="font-medium text-gray-900">報價資料</h4>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">報價單號: {quotation.quotation_number}</p>
                <p className="text-sm text-gray-600">創建人: {quotation.created_by || '未知'}</p>
                <p className="text-sm text-gray-600">創建時間: {quotation.created_at ? formatDateTime(quotation.created_at) : '未知'}</p>
                <p className="text-sm text-gray-600">最後更新: {quotation.updated_at ? formatDateTime(quotation.updated_at) : '未知'}</p>
              </div>
            </div>
            
            {/* Financial Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="bg-purple-100 p-2 rounded-full mr-3">
                  <DollarSign className="h-5 w-5 text-purple-600" />
                </div>
                <h4 className="font-medium text-gray-900">財務資料</h4>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">貨幣: {quotation.currency || 'TWD'}</p>
                <p className="text-sm text-gray-600">小計: {formatCurrency(quotation.subtotal || quotation.total_amount)}</p>
                <p className="text-sm text-gray-600">折扣: {formatCurrency(quotation.discount_amount || 0)}</p>
                <p className="text-sm text-gray-600">稅額: {formatCurrency(quotation.tax_amount || 0)}</p>
                <p className="text-sm font-medium text-gray-900">總額: {formatCurrency(quotation.total_amount)}</p>
              </div>
            </div>
          </div>
          
          {/* Notes */}
          {(quotation.notes || quotation.internal_notes) && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {quotation.notes && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">客戶備註</h4>
                  <p className="text-sm text-gray-600">{quotation.notes}</p>
                </div>
              )}
              
              {quotation.internal_notes && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">內部備註</h4>
                  <p className="text-sm text-gray-600">{quotation.internal_notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Quotation Items */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-800">報價項目</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">產品</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">描述</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">數量</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">單價</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">稅率</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">小計</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">稅額</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">總計</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {quotation.items && quotation.items.map((item, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.product_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{item.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">{item.quantity} {item.unit}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">{formatCurrency(item.unit_price)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">{formatPercentage(item.tax_rate || 0)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">{formatCurrency(item.subtotal)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">{formatCurrency(item.tax_amount || 0)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">{formatCurrency(item.total || item.subtotal)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan="5" className="px-6 py-4 text-sm font-medium text-gray-900 text-right">小計:</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">{formatCurrency(quotation.subtotal || quotation.total_amount)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">{formatCurrency(quotation.tax_amount || 0)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">{formatCurrency(quotation.total_amount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      
      {/* Conversion Message */}
      {conversionMessage && (
        <div className={`p-4 rounded-lg ${
          conversionMessage.includes('成功') 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {conversionMessage}
        </div>
      )}

      {/* Tracing Information */}
      {showTraceInfo && convertedSalesOrder && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-2 rounded-full">
                <CheckCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-900 mb-1">
                  轉換完成 - 追蹤資訊
                </h3>
                <p className="text-sm text-blue-700">
                  報價單已成功轉換為銷售訂單 (狀態: SO)，您可以追蹤新建立的訂單
                </p>
              </div>
            </div>
            <button
              onClick={handleHideTraceInfo}
              className="text-blue-400 hover:text-blue-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Sales Order Info */}
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-2 mb-3">
                <Package className="h-4 w-4 text-blue-600" />
                <h4 className="font-medium text-blue-900">銷售訂單資訊</h4>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">訂單編號:</span>
                  <span className="font-medium text-blue-900">{convertedSalesOrder.sales_order_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">客戶:</span>
                  <span className="font-medium text-blue-900">{convertedSalesOrder.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">總金額:</span>
                  <span className="font-medium text-blue-900">{formatCurrency(convertedSalesOrder.total_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">狀態:</span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                    草稿
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-2 mb-3">
                <ExternalLink className="h-4 w-4 text-blue-600" />
                <h4 className="font-medium text-blue-900">快速操作</h4>
              </div>
              <div className="space-y-3">
                <button
                  onClick={handleViewSalesOrder}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>查看銷售訂單</span>
                </button>
                <button
                  onClick={handleCopySONumber}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>複製訂單編號</span>
                </button>
              </div>
            </div>
          </div>

          {/* Process Timeline */}
          <div className="mt-4 bg-white p-4 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-2 mb-3">
              <Clock className="h-4 w-4 text-blue-600" />
              <h4 className="font-medium text-blue-900">轉換流程</h4>
            </div>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">報價單驗證</span>
              </div>
              <div className="flex-1 h-px bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">資料轉換</span>
              </div>
              <div className="flex-1 h-px bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">銷售訂單建立</span>
              </div>
              <div className="flex-1 h-px bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-blue-600 font-medium">完成</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end space-x-4">
        {/* Print Menu */}
        <div className="relative">
          <button 
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-2"
            onClick={() => setShowPrintMenu(!showPrintMenu)}
          >
            <Printer className="h-4 w-4" />
            <span>列印報價單</span>
          </button>
          
          {showPrintMenu && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              <div className="py-1">
                <button 
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  onClick={handlePrint}
                >
                  <Printer className="h-4 w-4" />
                  <span>直接列印</span>
                </button>
                <button 
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  onClick={handleDownloadPDF}
                >
                  <Download className="h-4 w-4" />
                  <span>下載 PDF</span>
                </button>
                <button 
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  onClick={handleDownloadExcel}
                >
                  <Download className="h-4 w-4" />
                  <span>下載 Excel</span>
                </button>
                <button 
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  onClick={handleDownloadWord}
                >
                  <Download className="h-4 w-4" />
                  <span>下載 Word</span>
                </button>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <button 
            className={`px-4 py-2 rounded-md transition-colors flex items-center space-x-2 ${
              (quotation.status_id === 'ACCEPTED' || quotation.status_id === 'SO') && !isConverting
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-400 text-gray-200 cursor-not-allowed'
            }`}
            onClick={handleConvertToOrder}
            disabled={(quotation.status_id !== 'ACCEPTED' && quotation.status_id !== 'SO') || isConverting}
          >
            <ArrowRight className="h-4 w-4" />
            <span>{isConverting ? '轉換中...' : quotation.status_id === 'SO' ? '重新轉換為銷售訂單' : '轉換為銷售訂單'}</span>
          </button>
          
          {/* Tracing Info Icon */}
          {convertedSalesOrder && (
            <div className="relative">
              <button
                onClick={() => setShowTraceInfo(!showTraceInfo)}
                className="p-2 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200 transition-colors relative"
                title="查看轉換追蹤資訊"
              >
                <Info className="h-4 w-4" />
                {/* Notification Badge */}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Print menu backdrop */}
      {showPrintMenu && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowPrintMenu(false)}
        />
      )}
    </div>
  );
};

export default QuotationDetail;
